import {
  AlertCircle,
  CheckCircle,
  Clock,
  Edit3,
  Heart,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import React, { useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import {
  healthConditionsStrings,
  type HealthConditionsStrings,
  isBuiltInLabel,
} from "../../locales/modules/healthConditions";
import { useI18nStore } from "../../stores/i18nStore";
import { MedicalCondition } from "../../types/fhir-health";

interface MedicalConditionsProps {
  conditions: MedicalCondition[];
  onSave: (conditions: MedicalCondition[]) => void;
  isEditing: boolean;
  onToggleEdit: () => void;
  isSaving: boolean;
}

// Common travel-relevant medical conditions with ICD-10 codes. The code is
// what identifies the entry; the name is a translation key, and the English
// label is what gets stored as the record's display text.
type ConditionCategory = MedicalCondition["category"];
type ConditionNameKey = keyof HealthConditionsStrings;

const COMMON_CONDITIONS: {
  icd: string;
  nameKey: ConditionNameKey;
  category: ConditionCategory;
}[] = [
  {
    icd: "Z87.891",
    nameKey: "condNameDiabetes",
    category: "problem-list-item",
  },
  {
    icd: "I10",
    nameKey: "condNameHypertension",
    category: "problem-list-item",
  },
  { icd: "J45", nameKey: "condNameAsthma", category: "problem-list-item" },
  {
    icd: "I25.9",
    nameKey: "condNameCoronaryArteryDisease",
    category: "problem-list-item",
  },
  { icd: "K30", nameKey: "condNameGastritis", category: "encounter-diagnosis" },
  {
    icd: "M79.3",
    nameKey: "condNameBackPain",
    category: "encounter-diagnosis",
  },
  { icd: "F32.9", nameKey: "condNameDepression", category: "health-concern" },
  {
    icd: "F41.9",
    nameKey: "condNameAnxietyDisorder",
    category: "health-concern",
  },
  {
    icd: "E78.0",
    nameKey: "condNameHighCholesterol",
    category: "problem-list-item",
  },
  {
    icd: "M06.9",
    nameKey: "condNameRheumatoidArthritis",
    category: "problem-list-item",
  },
  { icd: "J44.1", nameKey: "condNameCopd", category: "problem-list-item" },
  {
    icd: "N18.9",
    nameKey: "condNameKidneyDisease",
    category: "problem-list-item",
  },
  {
    icd: "K59.0",
    nameKey: "condNameConstipation",
    category: "encounter-diagnosis",
  },
  { icd: "H52.1", nameKey: "condNameMyopia", category: "encounter-diagnosis" },
  {
    icd: "D50.9",
    nameKey: "condNameIronDeficiency",
    category: "encounter-diagnosis",
  },
  {
    icd: "B02.9",
    nameKey: "condNameShingles",
    category: "encounter-diagnosis",
  },
];

const CATEGORY_KEYS: Record<string, ConditionNameKey> = {
  "problem-list-item": "condCategoryProblemListItem",
  "encounter-diagnosis": "condCategoryEncounterDiagnosis",
  "health-concern": "condCategoryHealthConcern",
};

const CATEGORY_BADGE_KEYS: Record<string, ConditionNameKey> = {
  "problem-list-item": "condBadgeChronic",
  "encounter-diagnosis": "condBadgeAcute",
  "health-concern": "condBadgeRisk",
};

const CLINICAL_STATUS_KEYS: Record<string, ConditionNameKey> = {
  active: "condStatusActive",
  inactive: "condStatusInactive",
  resolved: "condStatusResolved",
  remission: "condStatusRemission",
  recurrence: "condStatusRecurrence",
  relapse: "condStatusRelapse",
};

const VERIFICATION_STATUS_KEYS: Record<string, ConditionNameKey> = {
  confirmed: "condVerificationConfirmed",
  unconfirmed: "condVerificationUnconfirmed",
  provisional: "condVerificationProvisional",
  differential: "condVerificationDifferential",
  refuted: "condVerificationRefuted",
  "entered-in-error": "condVerificationEnteredInError",
};

const SEVERITY_KEYS: Record<string, ConditionNameKey> = {
  mild: "condSeverityMild",
  moderate: "condSeverityModerate",
  severe: "condSeveritySevere",
};

export const MedicalConditions: React.FC<MedicalConditionsProps> = ({
  conditions,
  onSave,
  isEditing,
  onToggleEdit,
  isSaving,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const label = (map: Record<string, ConditionNameKey>, code?: string) => {
    const key = code ? map[code] : undefined;
    return key ? t[key] : (code ?? "");
  };

  // Built-in conditions are shown in the active language, also for records
  // that older versions stored with a German name; free text stays as typed.
  const conditionName = (condition: MedicalCondition) => {
    const entry = COMMON_CONDITIONS.find((c) => c.icd === condition.code);
    if (entry && isBuiltInLabel(entry.nameKey, condition.display)) {
      return t[entry.nameKey];
    }
    return condition.display;
  };
  const [localConditions, setLocalConditions] =
    useState<MedicalCondition[]>(conditions);

  const addNewCondition = () => {
    const todayDate = new Date().toISOString().split("T")[0];
    const newCondition: MedicalCondition = {
      id: crypto.randomUUID(),
      code: "",
      display: "",
      category: "problem-list-item",
      clinicalStatus: "active",
      verificationStatus: "confirmed",
      ...(todayDate && { recordedDate: todayDate }),
    };
    setLocalConditions([...localConditions, newCondition]);
  };

  const updateCondition = (
    index: number,
    field: keyof MedicalCondition,
    value: string | boolean | Date,
  ) => {
    const updated = [...localConditions];
    const current = updated[index];

    if (field === "code") {
      // Auto-fill condition name and category
      const condition = COMMON_CONDITIONS.find((c) => c.icd === value);
      if (condition && current) {
        updated[index] = {
          ...current,
          code: value,
          display: healthConditionsStrings.en[condition.nameKey],
          category: condition.category,
        } as MedicalCondition;
      } else if (current) {
        updated[index] = { ...current, [field]: value } as MedicalCondition;
      }
    } else if (current) {
      updated[index] = { ...current, [field]: value } as MedicalCondition;
    }
    setLocalConditions(updated);
  };

  const removeCondition = (index: number) => {
    const updated = localConditions.filter((_, i) => i !== index);
    setLocalConditions(updated);
  };

  const handleSave = () => {
    onSave(localConditions);
  };

  const getStatusColor = (
    clinicalStatus?: string,
    verificationStatus?: string,
  ) => {
    if (
      verificationStatus === "refuted" ||
      verificationStatus === "entered-in-error"
    ) {
      return "text-red-600 bg-red-50 border-red-200";
    }
    switch (clinicalStatus) {
      case "active":
        return "text-red-600 bg-red-50 border-red-200";
      case "resolved":
        return "text-green-600 bg-green-50 border-green-200";
      case "inactive":
        return "text-gray-600 bg-gray-50 border-gray-200";
      case "remission":
        return "text-primary-600 bg-primary-50 border-primary-200";
      default:
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
    }
  };

  const getStatusIcon = (
    clinicalStatus?: string,
    verificationStatus?: string,
  ) => {
    if (
      verificationStatus === "refuted" ||
      verificationStatus === "entered-in-error"
    ) {
      return <AlertCircle className="w-4 h-4" />;
    }
    switch (clinicalStatus) {
      case "active":
        return <AlertCircle className="w-4 h-4" />;
      case "resolved":
        return <CheckCircle className="w-4 h-4" />;
      case "inactive":
        return <Clock className="w-4 h-4" />;
      case "remission":
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case "severe":
        return "text-red-700";
      case "moderate":
        return "text-yellow-700";
      case "mild":
        return "text-green-700";
      default:
        return "text-gray-700";
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <Heart className="w-5 h-5 text-red-600" />
          <span>{t.condTitle}</span>
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={onToggleEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
            aria-label={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {localConditions.map((condition, index) => (
            <div
              key={condition.id || index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label
                    htmlFor={`condition-${index}-code`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldCondition}
                  </label>
                  <select
                    id={`condition-${index}-code`}
                    value={condition.code}
                    onChange={(e) =>
                      updateCondition(index, "code", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {COMMON_CONDITIONS.map((cond) => (
                      <option key={cond.icd} value={cond.icd}>
                        {t[cond.nameKey]} ({t.condIcd}: {cond.icd})
                      </option>
                    ))}
                  </select>
                  {condition.code &&
                    !COMMON_CONDITIONS.find(
                      (c) => c.icd === condition.code,
                    ) && (
                      <input
                        type="text"
                        value={condition.display}
                        onChange={(e) =>
                          updateCondition(index, "display", e.target.value)
                        }
                        className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t.condCustomPlaceholder}
                      />
                    )}
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-category`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldCategory}
                  </label>
                  <select
                    id={`condition-${index}-category`}
                    value={condition.category}
                    onChange={(e) =>
                      updateCondition(index, "category", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(CATEGORY_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-clinical-status`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldClinicalStatus}
                  </label>
                  <select
                    id={`condition-${index}-clinical-status`}
                    value={condition.clinicalStatus}
                    onChange={(e) =>
                      updateCondition(index, "clinicalStatus", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(CLINICAL_STATUS_KEYS).map(
                      ([value, key]) => (
                        <option key={value} value={value}>
                          {t[key]}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-verification-status`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldVerificationStatus}
                  </label>
                  <select
                    id={`condition-${index}-verification-status`}
                    value={condition.verificationStatus}
                    onChange={(e) =>
                      updateCondition(
                        index,
                        "verificationStatus",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(VERIFICATION_STATUS_KEYS).map(
                      ([value, key]) => (
                        <option key={value} value={value}>
                          {t[key]}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-severity`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldSeverity}
                  </label>
                  <select
                    id={`condition-${index}-severity`}
                    value={condition.severity || ""}
                    onChange={(e) =>
                      updateCondition(index, "severity", e.target.value || "")
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t.condSeverityNone}</option>
                    {Object.entries(SEVERITY_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-onset`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldOnset}
                  </label>
                  <input
                    id={`condition-${index}-onset`}
                    type="date"
                    value={condition.onsetDateTime?.split("T")[0] || ""}
                    onChange={(e) =>
                      updateCondition(index, "onsetDateTime", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-abatement`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldAbatement}
                  </label>
                  <input
                    id={`condition-${index}-abatement`}
                    type="date"
                    value={condition.abatementDateTime?.split("T")[0] || ""}
                    onChange={(e) =>
                      updateCondition(
                        index,
                        "abatementDateTime",
                        e.target.value,
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`condition-${index}-recorder`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldRecorder}
                  </label>
                  <input
                    id={`condition-${index}-recorder`}
                    type="text"
                    value={condition.recorder || ""}
                    onChange={(e) =>
                      updateCondition(index, "recorder", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.condRecorderPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`condition-${index}-note`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.condFieldNote}
                  </label>
                  <textarea
                    id={`condition-${index}-note`}
                    value={condition.note || ""}
                    onChange={(e) =>
                      updateCondition(index, "note", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.condNotePlaceholder}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => removeCondition(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t.condRemove}
                  aria-label={t.condRemove}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addNewCondition}
            className="w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t.condAdd}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {conditions.length === 0 ? (
            <p className="text-gray-500 italic">{t.condEmpty}</p>
          ) : (
            conditions.map((condition, index) => {
              const statusColor = getStatusColor(
                condition.clinicalStatus,
                condition.verificationStatus,
              );
              const statusIcon = getStatusIcon(
                condition.clinicalStatus,
                condition.verificationStatus,
              );
              const severityColor = getSeverityColor(condition.severity);

              return (
                <div
                  key={condition.id || index}
                  className={`border rounded-lg p-4 ${statusColor}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                          {statusIcon}
                          <span>{conditionName(condition)}</span>
                        </h4>
                        <div className="flex items-center space-x-2">
                          {condition.severity && (
                            <span
                              className={`text-xs px-2 py-1 rounded bg-white bg-opacity-50 ${severityColor}`}
                            >
                              {label(SEVERITY_KEYS, condition.severity)}
                            </span>
                          )}
                          <div className="text-xs px-2 py-1 rounded bg-white bg-opacity-50">
                            {label(CATEGORY_BADGE_KEYS, condition.category)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 text-sm">
                        <span
                          className={`font-medium ${
                            condition.clinicalStatus === "active"
                              ? "text-red-700"
                              : condition.clinicalStatus === "resolved"
                                ? "text-green-700"
                                : condition.clinicalStatus === "inactive"
                                  ? "text-gray-700"
                                  : "text-primary-700"
                          }`}
                        >
                          {label(
                            CLINICAL_STATUS_KEYS,
                            condition.clinicalStatus,
                          )}
                        </span>

                        {condition.verificationStatus &&
                          condition.verificationStatus !== "confirmed" && (
                            <span className="ml-3 text-xs text-gray-600">
                              (
                              {label(
                                VERIFICATION_STATUS_KEYS,
                                condition.verificationStatus,
                              )}
                              )
                            </span>
                          )}

                        {condition.onsetDateTime && (
                          <span className="ml-3 text-gray-600">
                            {t.condSince.replace(
                              "{date}",
                              fmt.date(condition.onsetDateTime),
                            )}
                          </span>
                        )}

                        {condition.abatementDateTime && (
                          <span className="ml-3 text-gray-600">
                            {t.condUntil.replace(
                              "{date}",
                              fmt.date(condition.abatementDateTime),
                            )}
                          </span>
                        )}
                      </div>

                      {condition.recorder && (
                        <div className="mt-1 text-xs text-gray-600">
                          {t.condTreatingPhysician.replace(
                            "{name}",
                            condition.recorder,
                          )}
                        </div>
                      )}

                      {condition.note && (
                        <div className="mt-2 text-sm text-gray-700 italic">
                          {condition.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
